import type { PageHelp } from "../types";

export const learningPageHelp: Record<string, PageHelp> = {
  wiki: {
    title: "Wiki",
    summary:
      "Your agency's shared knowledge base: SOPs, playbooks and reference pages. Pages are grouped into spaces (folders) and can contain {{tokens}} that fill in with the active account's details.",
    steps: [
      { title: "Browse", body: "Pages are listed under their space, or under Unfiled. Click a page title to read it." },
      { title: "Make a folder (optional)", body: "If you can edit the wiki, click New space, type a Name, and optionally an Icon (emoji), then Create." },
      { title: "Start a page", body: "Click New page, type a Title, pick a Space (or Unfiled) and click Create. You land in the editor, and the page starts as a draft." },
      { title: "Write", body: "Use the toolbar for headings, bold, lists, quotes and links. Type tokens such as {{shopName}} where you want an account detail to appear." },
      { title: "Save", body: "Click Save. The button says Saved when there is nothing new to save." },
      { title: "Check it", body: "Click Preview to see the page with tokens filled in for the active account." },
      { title: "Publish", body: "Click Publish so the rest of the team can see it. Unpublish turns it back into a draft." },
    ],
    tips: [
      "Only people with wiki edit rights see New space, New page, Edit and the Show drafts box. By default that is owners and admins.",
      "Pick an account in the header before you Preview. Without one, tokens stay as typed.",
      "Saved pages keep the raw tokens. The filled-in values are only for display.",
    ],
    articles: ["learning-wiki-write-edit", "learning-wiki-tokens", "learning-home-tour"],
  },

  coaching: {
    title: "Coaching",
    summary:
      "Constantine's feedback on the calls he reviewed: the biggest recurring gaps, the detail by theme with the wording to use, and each mistake with its fix.",
    steps: [
      { title: "Start with the gaps", body: "The box at the top lists the four gaps that keep coming back. Work on those first." },
      { title: "Read by theme", body: "The detail, by theme explains each one with examples from the calls and the exact wording to use." },
      { title: "Check a call", body: "On Mistakes and fixes, tap a name to see only the mistakes from that call. Recurring ones have a heavier border." },
      { title: "Use it live", body: "The Live Call card turns all of this into boxes and checks you fill in during the call." },
    ],
    tips: ["Tap Fathom recording on a call to rewatch it."],
    articles: ["sales-run-a-live-call"],
  },

  playbook: {
    title: "Playbook",
    summary:
      "The structure of a sales call: what to do before you dial, then the four phases (Intro, Discovery, Pitch, Close) with the exact wording for the moments that decide calls.",
    steps: [
      { title: "Pick an account", body: "Choose the account in the header so script lines show its real details. Without one you see generic {{tokens}}." },
      { title: "Read the four things", body: "The strip at the top lists the four habits that decide a call. Drill Range and Mastery track them." },
      { title: "Do Before the call", body: "Work through the four numbered checks. The highlighted one, calling the business first, is marked highest value." },
      { title: "Follow the phases", body: "Read each phase in order: Intro, Discovery, Pitch, Close. The minute range shows where it belongs on a 60-minute call." },
      { title: "Watch the tags", body: "Each line is tagged say, ask, do or note. Lines marked must cover should not be skipped." },
      { title: "Search", body: "Type in Search the playbook to filter lines by their wording or coaching note." },
      { title: "Copy a line", body: "Click the copy icon on a line to copy it with tokens filled in." },
    ],
    tips: [
      "Blue words are filled from the account. Red words are tokens that could not be filled, and would be read out literally on a Voice AI call.",
      "It is a sequence, not a word-for-word script. Make the wording your own.",
      "For the live version with a timer, use Live Call.",
    ],
    articles: ["learning-playbook-find-things", "learning-wiki-tokens", "learning-run-a-drill"],
  },

  "drill-range": {
    title: "Drill Range",
    summary:
      "Practice sales calls against ten pretend prospects (personas), by voice or by text, then score yourself and log the result. Logged drills power your Mastery ranks.",
    steps: [
      { title: "Choose a persona", body: "Click any persona card. Turn on Suggested path to number them in a recommended order (built-in personas only)." },
      { title: "Launch the roleplay", body: "For voice, click a button such as Full Call to open an ElevenLabs page in a new tab. For text, click Copy text-roleplay prompt." },
      { title: "Play the call", body: "For text, paste the prompt into ChatGPT or Claude, say start, and reply as the rep. Say SCORE at the end to get feedback." },
      { title: "Score yourself", body: "Back in Flowio, move the Overall slider from 0 to 100 and tick each of the four things you really did." },
      { title: "Walk-away personas", body: "On Jerry and Angela, tick Correctly walked away + asked for referral only if you declined the sale." },
      { title: "Log it", body: "Click Log drill. The top of the page updates Drills run, Avg score and Mastered." },
    ],
    tips: [
      "Nothing is scored automatically. The number you set is the number that counts, so be honest.",
      "Flowio needs no API keys for drills. Voice uses an outside ElevenLabs page and text uses your own AI chat.",
      "The Log drill button always saves the drill as a text drill, even after a voice session.",
      "Drills are shared across your agency, so the totals include every rep.",
    ],
    articles: ["learning-run-a-drill", "learning-personas-scoring", "learning-mastery-ranks"],
  },

  objections: {
    title: "Objections",
    summary:
      "Instant reference cards for the pushback you hear on calls. Each one is really about funds, timing, a partner, or uncertainty. Isolate the real reason first, and never discount.",
    steps: [
      { title: "Pick an account", body: "Choose one in the header so the lines fill in with the prospect's details." },
      { title: "Find the card", body: "Match what the prospect said to the quoted phrase on a card, for example That's more than I was thinking." },
      { title: "Make the first move", body: "Read the blue first-move line under the phrase. That is your opening response." },
      { title: "Use the lines", body: "Work through the labelled lines in order, such as The split or Uncover. Click the copy icon to copy one." },
      { title: "Heed the red warning", body: "If a card shows a red Never note, treat it as a hard rule." },
      { title: "Know when to walk", body: "At the bottom, the walk-away script shows how to politely decline a bad fit and ask for a referral." },
    ],
    tips: [
      "Press Ctrl+K (or Cmd+K), or type /, anywhere in the app to open search and jump straight to an objection card.",
      "Red words in a line are tokens that are not filled in. Select an account to fix them.",
      "Hearing partner objections at the close means the question was missed in Discovery.",
    ],
    articles: ["learning-objection-cards", "learning-playbook-find-things", "learning-wiki-tokens"],
  },

  mastery: {
    title: "Mastery",
    summary:
      "A five-rank ladder to Certified. Every rank is unlocked by numbers from your logged drills, not by opinion.",
    steps: [
      { title: "Read the ring", body: "The circle shows how many of the 5 ranks you have passed, and which rank is Next." },
      { title: "Check the four things", body: "The meters show how often each habit was ticked across all logged drills. A dash means no drills yet." },
      { title: "Read each gate", body: "Each rank card states its exact requirement. A tick and Passed badge mean you meet it right now." },
      { title: "Go drill", body: "Click Go drill to open Drill Range and log more drills." },
      { title: "Aim for Certified", body: "Rank 5 needs two correct walk-aways and your last three scored drills averaging 80 or more." },
    ],
    tips: [
      "Each rank is checked separately against your current drills, so a rank can show as passed or not passed at any time.",
      "Ranks 2 to 4 count different personas, so repeating one persona will not raise them.",
      "Drills are shared across the agency, so these numbers reflect every rep, not just you.",
    ],
    articles: ["learning-mastery-ranks", "learning-run-a-drill", "learning-personas-scoring"],
  },
};
